import React from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Box,
  Divider,
  Icon,
  Link,
  Button,
  Grid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiExternalLink,
  FiBarChart2,
  FiMapPin,
  FiDatabase,
  FiUser,
  FiBriefcase,
  FiPhone,
  FiMail,
  FiGlobe,
  FiSearch,
  FiAward,
  FiCalendar,
  FiHash,
  FiLinkedin,
} from 'react-icons/fi';
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
} from 'react-icons/fa';
import moment from 'moment';

// Reusable field display: shows label + value, with "---" for empty
function RetrField({ label, value, isLink, isEmail, isCurrency, isNumber }) {
  const emptyColor = useColorModeValue('gray.400', 'gray.500');

  let displayValue = value;
  if (isCurrency && value != null) {
    const num = Number(value);
    if (!isNaN(num)) {
      displayValue = num >= 1000000
        ? `$${(num / 1000000).toFixed(1)}M`
        : num >= 1000
          ? `$${(num / 1000).toFixed(0)}K`
          : `$${num.toLocaleString()}`;
    }
  } else if (isNumber && value != null) {
    displayValue = Number(value).toLocaleString();
  }

  const hasValue = value != null && value !== '' && value !== 0;

  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={0.5}>{label}</Text>
      {hasValue ? (
        isLink ? (
          <Link href={value} isExternal color="teal.500" fontSize="sm" wordBreak="break-all">
            {displayValue || value} <Icon as={FiExternalLink} boxSize={3} ml={1} />
          </Link>
        ) : isEmail ? (
          <Link href={`mailto:${value}`} color="teal.500" fontSize="sm">{value}</Link>
        ) : (
          <Text fontSize="sm" fontWeight="medium">{displayValue}</Text>
        )
      ) : (
        <Text fontSize="sm" color={emptyColor}>---</Text>
      )}
    </Box>
  );
}

function SectionHeader({ children, icon }) {
  return (
    <HStack spacing={2} mt={2}>
      {icon && <Icon as={icon} color="teal.500" boxSize={4} />}
      <Text fontSize="sm" fontWeight="bold" color="teal.600" textTransform="uppercase" letterSpacing="wider">
        {children}
      </Text>
    </HStack>
  );
}

function SocialLink({ url, label, icon, colorScheme = 'gray' }) {
  const hasValue = url && url.trim();
  const emptyColor = useColorModeValue('gray.400', 'gray.500');
  return (
    <HStack spacing={2}>
      <Icon as={icon} color={hasValue ? `${colorScheme}.500` : 'gray.300'} boxSize={4} />
      {hasValue ? (
        <Link href={url} isExternal color={`${colorScheme}.500`} fontSize="sm" noOfLines={1} maxW="250px">
          {label} <Icon as={FiExternalLink} boxSize={3} />
        </Link>
      ) : (
        <Text fontSize="sm" color={emptyColor}>---</Text>
      )}
    </HStack>
  );
}

function SearchLinkButton({ url, label }) {
  if (!url) return null;
  return (
    <Link href={url} isExternal _hover={{ textDecoration: 'none' }}>
      <Button size="xs" variant="outline" colorScheme="teal" rightIcon={<FiExternalLink />}>
        {label}
      </Button>
    </Link>
  );
}

export default function RetrTabPanel({ contact }) {
  const infoBG = useColorModeValue('gray.50', 'gray.700');
  const cardBorder = useColorModeValue('teal.100', 'teal.700');

  const licenses = Array.isArray(contact.retrLicenses) ? contact.retrLicenses : [];
  const employmentHistory = Array.isArray(contact.retrEmploymentHistory) ? contact.retrEmploymentHistory : [];
  const branches = Array.isArray(contact.retrBranches) ? contact.retrBranches : [];
  const personality = contact.retrPersonality || null;
  const hasSearchLinks = contact.retrGoogleSearchUrl || contact.retrFacebookSearchUrl ||
    contact.retrLinkedinSearchUrl || contact.retrZillowSearchUrl || contact.retrNmlsLookupUrl;

  return (
    <VStack align="stretch" spacing={5}>
      {/* Header Card */}
      <Box p={4} bg={infoBG} borderRadius="lg" borderWidth="1px" borderColor={cardBorder}>
        <HStack justify="space-between" flexWrap="wrap" spacing={3}>
          <HStack spacing={3} flexWrap="wrap">
            <Icon as={FiBarChart2} color="teal.500" boxSize={5} />
            <Heading size="sm" color="teal.700">RETR Intelligence</Heading>
            {contact.retrNmlsId && (
              <Badge colorScheme="gray" fontFamily="mono">NMLS #{contact.retrNmlsId}</Badge>
            )}
            {contact.retrLoType && (
              <Badge colorScheme={
                contact.retrLoType === 'Broker' ? 'purple' :
                contact.retrLoType === 'Bank/Cu' ? 'blue' : 'teal'
              }>
                {contact.retrLoType}
              </Badge>
            )}
            {contact.retrLicensedSince && (
              <Badge colorScheme="green" variant="subtle">Licensed since {contact.retrLicensedSince}</Badge>
            )}
          </HStack>
          <HStack spacing={2}>
            {contact.retrImportedAt && (
              <Tooltip label={moment(contact.retrImportedAt).format('MMM D, YYYY h:mm A')}>
                <HStack spacing={1} color="gray.500">
                  <Icon as={FiDatabase} boxSize={3} />
                  <Text fontSize="xs">Updated {moment(contact.retrImportedAt).fromNow()}</Text>
                </HStack>
              </Tooltip>
            )}
            {contact.retrProfileUrl && (
              <Link href={contact.retrProfileUrl} isExternal _hover={{ textDecoration: 'none' }}>
                <Button size="sm" colorScheme="teal" variant="outline" rightIcon={<FiExternalLink />}>
                  RETR Profile
                </Button>
              </Link>
            )}
          </HStack>
        </HStack>
      </Box>

      {/* Identity & Licensing */}
      <Box>
        <SectionHeader icon={FiUser}>Identity &amp; Licensing</SectionHeader>
        <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <RetrField label="RETR Profile ID" value={contact.retrProfileId} />
            <RetrField label="NMLS ID" value={contact.retrNmlsId} />
            <RetrField label="Licensed Since" value={contact.retrLicensedSince} />
            <RetrField label="Job Title / Role" value={contact.retrJobTitle} />
            <RetrField label="LO Type" value={contact.retrLoType} />
            <RetrField label="RETR Profile URL" value={contact.retrProfileUrl} isLink />
          </Grid>
        </Box>
      </Box>

      {/* Contact Info (RETR) */}
      <Box>
        <SectionHeader icon={FiPhone}>Contact Info (RETR)</SectionHeader>
        <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <RetrField label="Work Email" value={contact.retrWorkEmail} isEmail />
            <RetrField label="Personal Email" value={contact.retrPersonalEmail} isEmail />
            <RetrField label="Office Phone" value={contact.retrOfficePhone} />
            <RetrField label="Mobile Phone" value={contact.retrMobilePhone} />
          </Grid>
        </Box>
      </Box>

      {/* Current Employer */}
      <Box>
        <SectionHeader icon={FiBriefcase}>Current Employer</SectionHeader>
        <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <RetrField label="Company Name" value={contact.retrCompanyName} />
            <RetrField label="Office Location" value={contact.retrOfficeLocation || contact.retrLocation} />
            <RetrField label="Employer Start Date" value={contact.retrEmployerStartDate} />
            <RetrField label="Branch Profile" value={contact.retrBranchUrl} isLink />
          </Grid>
        </Box>
      </Box>

      {/* Production Stats */}
      <Box>
        <SectionHeader icon={FiBarChart2}>Production Stats</SectionHeader>
        <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={2}>
          {/* State Production */}
          <Box p={4} bg={infoBG} borderRadius="md">
            <Text fontSize="xs" fontWeight="bold" color="teal.600" textTransform="uppercase" letterSpacing="wider" mb={3}>
              {contact.retrState || 'State'} Production
            </Text>
            <VStack align="stretch" spacing={2}>
              <RetrField label="Loan Count" value={contact.retrLoanCount} isNumber />
              <RetrField label="Loan Volume" value={contact.retrLoanVolume} isCurrency />
              <Divider />
              <Text fontSize="xs" color="gray.500" fontWeight="semibold">Product Mix</Text>
              <HStack spacing={2} flexWrap="wrap">
                <Badge size="sm" colorScheme="blue" variant="subtle">
                  Conv {contact.retrConvCount != null ? contact.retrConvCount.toLocaleString() : '---'}
                </Badge>
                <Badge size="sm" colorScheme="green" variant="subtle">
                  VA {contact.retrVaCount != null ? contact.retrVaCount.toLocaleString() : '---'}
                </Badge>
                <Badge size="sm" colorScheme="orange" variant="subtle">
                  FHA {contact.retrFhaCount != null ? contact.retrFhaCount.toLocaleString() : '---'}
                </Badge>
              </HStack>
            </VStack>
          </Box>

          {/* Nationwide Production */}
          <Box p={4} bg={infoBG} borderRadius="md">
            <Text fontSize="xs" fontWeight="bold" color="teal.600" textTransform="uppercase" letterSpacing="wider" mb={3}>
              Nationwide
            </Text>
            <VStack align="stretch" spacing={2}>
              <RetrField label="Total Loan Count" value={contact.retrTotalLoanCount} isNumber />
              <RetrField label="Total Loan Volume" value={contact.retrTotalLoanVolume} isCurrency />
              <RetrField label="Avg Loan Size" value={contact.retrAvgLoan} isCurrency />
              <Divider />
              <RetrField label="Buyer Agents" value={contact.retrBuyerAgentsCount} isNumber />
              <RetrField label="Years of Experience" value={contact.retrYearsExperience} isNumber />
              <RetrField label="State Licenses" value={contact.retrStateLicensesCount} isNumber />
              <RetrField label="Federal Registrations" value={contact.retrFederalRegistrationsCount} isNumber />
            </VStack>
          </Box>
        </Grid>
      </Box>

      {/* Social & Web Presence */}
      <Box>
        <SectionHeader icon={FiGlobe}>Social &amp; Web Presence</SectionHeader>
        <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <SocialLink url={contact.retrLinkedinUrl} label="LinkedIn" icon={FiLinkedin} colorScheme="linkedin" />
            <SocialLink url={contact.retrFacebookUrl} label="Facebook" icon={FaFacebook} colorScheme="facebook" />
            <SocialLink url={contact.retrTwitterUrl} label="Twitter / X" icon={FaTwitter} colorScheme="twitter" />
            <SocialLink url={contact.retrInstagramUrl} label="Instagram" icon={FaInstagram} colorScheme="pink" />
            <SocialLink url={contact.retrZillowUrl} label="Zillow" icon={FiGlobe} colorScheme="blue" />
            <SocialLink url={contact.retrWebsiteUrl} label="Website" icon={FiGlobe} colorScheme="teal" />
            <SocialLink url={contact.retrExperienceUrl} label="Experience / Resume" icon={FiAward} colorScheme="orange" />
          </Grid>
        </Box>
      </Box>

      {/* Search Links */}
      {hasSearchLinks && (
        <Box>
          <SectionHeader icon={FiSearch}>Quick Search Links</SectionHeader>
          <HStack spacing={2} mt={2} flexWrap="wrap">
            <SearchLinkButton url={contact.retrGoogleSearchUrl} label="Google" />
            <SearchLinkButton url={contact.retrFacebookSearchUrl} label="Facebook" />
            <SearchLinkButton url={contact.retrLinkedinSearchUrl} label="LinkedIn" />
            <SearchLinkButton url={contact.retrZillowSearchUrl} label="Zillow" />
            <SearchLinkButton url={contact.retrNmlsLookupUrl} label="NMLS Lookup" />
          </HStack>
        </Box>
      )}

      {/* Licenses Table */}
      <Box>
        <SectionHeader icon={FiAward}>Licenses</SectionHeader>
        {licenses.length > 0 ? (
          <Box overflowX="auto" mt={2} borderWidth="1px" borderRadius="md">
            <Table size="sm">
              <Thead bg={infoBG}>
                <Tr>
                  <Th fontSize="xs">State / Type</Th>
                  <Th fontSize="xs">Status</Th>
                  <Th fontSize="xs">Authorized</Th>
                  <Th fontSize="xs">Issue Date</Th>
                  <Th fontSize="xs">License #</Th>
                </Tr>
              </Thead>
              <Tbody>
                {licenses.map((lic, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="sm">{lic.type || lic.state || '---'}</Td>
                    <Td fontSize="sm">
                      <Badge colorScheme={lic.status === 'Active' ? 'green' : 'gray'} fontSize="xs">
                        {lic.status || '---'}
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{lic.authorized || '---'}</Td>
                    <Td fontSize="sm">{lic.issueDate || '---'}</Td>
                    <Td fontSize="sm" fontFamily="mono">{lic.number || '---'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
            <Text fontSize="sm" color="gray.500">No license data available</Text>
          </Box>
        )}
      </Box>

      {/* Employment History */}
      <Box>
        <SectionHeader icon={FiBriefcase}>Employment History</SectionHeader>
        {employmentHistory.length > 0 ? (
          <Box overflowX="auto" mt={2} borderWidth="1px" borderRadius="md">
            <Table size="sm">
              <Thead bg={infoBG}>
                <Tr>
                  <Th fontSize="xs">Employer</Th>
                  <Th fontSize="xs">Start Date</Th>
                  <Th fontSize="xs">End Date</Th>
                  <Th fontSize="xs">Employer NMLS</Th>
                </Tr>
              </Thead>
              <Tbody>
                {employmentHistory.map((emp, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="sm" fontWeight={!emp.endDate ? 'bold' : 'normal'}>{emp.employer || '---'}</Td>
                    <Td fontSize="sm">{emp.startDate || '---'}</Td>
                    <Td fontSize="sm">{emp.endDate || <Badge colorScheme="green" fontSize="xs">Current</Badge>}</Td>
                    <Td fontSize="sm" fontFamily="mono">{emp.employerNmlsId || '---'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
            <Text fontSize="sm" color="gray.500">No employment history available</Text>
          </Box>
        )}
      </Box>

      {/* Branches */}
      <Box>
        <SectionHeader icon={FiMapPin}>Branches</SectionHeader>
        {branches.length > 0 ? (
          <Box overflowX="auto" mt={2} borderWidth="1px" borderRadius="md">
            <Table size="sm">
              <Thead bg={infoBG}>
                <Tr>
                  <Th fontSize="xs">Branch</Th>
                  <Th fontSize="xs">Location</Th>
                  <Th fontSize="xs">NMLS ID</Th>
                  <Th fontSize="xs">Start Date</Th>
                  <Th fontSize="xs">LOs</Th>
                  <Th fontSize="xs">Manager</Th>
                </Tr>
              </Thead>
              <Tbody>
                {branches.map((br, idx) => (
                  <Tr key={idx}>
                    <Td fontSize="sm">{br.name || '---'}</Td>
                    <Td fontSize="sm">{br.location || '---'}</Td>
                    <Td fontSize="sm" fontFamily="mono">{br.nmlsId || '---'}</Td>
                    <Td fontSize="sm">{br.startDate || '---'}</Td>
                    <Td fontSize="sm">{br.loCount || '---'}</Td>
                    <Td fontSize="sm">
                      {br.isManager ? <Badge colorScheme="green" fontSize="xs">Yes</Badge> : '---'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
            <Text fontSize="sm" color="gray.500">No branch data available</Text>
          </Box>
        )}
      </Box>

      {/* Personality Assessment */}
      <Box>
        <SectionHeader icon={FiUser}>Personality Assessment</SectionHeader>
        {contact.retrHasPersonality && personality ? (
          <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
            <VStack align="stretch" spacing={3}>
              {personality.ocean && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>OCEAN Scores</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {Object.entries(personality.ocean).map(([trait, score]) => (
                      <Tooltip key={trait} label={`${trait}: ${score}`}>
                        <Badge colorScheme="teal" variant="subtle" px={2} py={1}>
                          {trait.charAt(0).toUpperCase()}: {score}
                        </Badge>
                      </Tooltip>
                    ))}
                  </HStack>
                </Box>
              )}
              {personality.disc && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>DISC Scores</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {Object.entries(personality.disc).map(([trait, score]) => (
                      <Tooltip key={trait} label={`${trait}: ${score}`}>
                        <Badge colorScheme="purple" variant="subtle" px={2} py={1}>
                          {trait.charAt(0).toUpperCase()}: {score}
                        </Badge>
                      </Tooltip>
                    ))}
                  </HStack>
                </Box>
              )}
              {personality.summary && (
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>AI Summary</Text>
                  <Text fontSize="sm" whiteSpace="pre-wrap">{personality.summary}</Text>
                </Box>
              )}
            </VStack>
          </Box>
        ) : (
          <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
            <Text fontSize="sm" color="gray.500">No personality assessment available</Text>
          </Box>
        )}
      </Box>

      {/* Metadata */}
      <Box>
        <SectionHeader icon={FiHash}>Metadata</SectionHeader>
        <Box p={4} bg={infoBG} borderRadius="md" mt={2}>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <RetrField label="Track Record Key" value={contact.retrTrackRecordKey} />
            <RetrField label="RETR State" value={contact.retrState} />
            <RetrField label="RETR Location" value={contact.retrLocation} />
            <RetrField label="Has Personality Data" value={contact.retrHasPersonality ? 'Yes' : 'No'} />
            <RetrField label="Last Imported" value={contact.retrImportedAt ? moment(contact.retrImportedAt).format('MMM D, YYYY h:mm A') : null} />
          </Grid>
        </Box>
      </Box>
    </VStack>
  );
}
